import React, { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { envState } from "./env";

// Build a rectangular ground plane (local u=x, v=y) with square holes cut out.
// After rotation [-PI/2,0,0] at position (px,_,pz): world_x = px+u, world_z = pz-v.
function holedGround(w, h, holes) {
  const shape = new THREE.Shape();
  shape.moveTo(-w / 2, -h / 2);
  shape.lineTo(w / 2, -h / 2);
  shape.lineTo(w / 2, h / 2);
  shape.lineTo(-w / 2, h / 2);
  shape.lineTo(-w / 2, -h / 2);
  holes.forEach(({ u, v, half }) => {
    const p = new THREE.Path();
    p.moveTo(u - half, v - half);
    p.lineTo(u + half, v - half);
    p.lineTo(u + half, v + half);
    p.lineTo(u - half, v + half);
    p.lineTo(u - half, v - half);
    shape.holes.push(p);
  });
  return new THREE.ShapeGeometry(shape);
}

const HOLE = 38; // pool opening half-extent (matches MemorialPool)

export default function Plaza() {
  const groundMat = useRef();

  // Base land with the two memorial pool voids physically cut out.
  const baseGeo = useMemo(
    () =>
      holedGround(2400, 2400, [
        { u: -46, v: 46, half: HOLE }, // North pool
        { u: 24, v: -40, half: HOLE }, // South pool
      ]),
    []
  );
  // Granite plaza inlay (centered at [-10,-20]) with matching voids.
  const inlayGeo = useMemo(
    () =>
      holedGround(330, 366, [
        { u: -36, v: 26, half: HOLE },
        { u: 34, v: -60, half: HOLE },
      ]),
    []
  );

  useFrame(() => {
    if (groundMat.current)
      groundMat.current.color.setScalar(0.22 + (1 - envState.night) * 0.2);
  });

  return (
    <group>
      {/* land / ground — voids cut where the memorial pools recess below grade */}
      <mesh geometry={baseGeo} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} receiveShadow>
        <meshStandardMaterial ref={groundMat} color="#7d7d78" roughness={0.95} side={THREE.DoubleSide} />
      </mesh>
      {/* Hudson River (west) */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[-640, 0.03, 60]}>
        <planeGeometry args={[560, 2000]} />
        <meshStandardMaterial color="#15414c" metalness={0.35} roughness={0.22} />
      </mesh>
      {/* East River (east) */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[820, 0.03, -20]}>
        <planeGeometry args={[620, 1900]} />
        <meshStandardMaterial color="#15414c" metalness={0.35} roughness={0.22} />
      </mesh>
      {/* memorial plaza granite inlay — pool openings cut out */}
      <mesh geometry={inlayGeo} rotation={[-Math.PI / 2, 0, 0]} position={[-10, 0.05, -20]} receiveShadow>
        <meshStandardMaterial color="#c4beb0" roughness={0.82} side={THREE.DoubleSide} />
      </mesh>
      {/* subtle boundary curbs */}
      <mesh position={[-10, 0.4, 180]}>
        <boxGeometry args={[360, 0.8, 2]} />
        <meshStandardMaterial color="#b8b0a2" />
      </mesh>
      <mesh position={[-10, 0.4, -220]}>
        <boxGeometry args={[360, 0.8, 2]} />
        <meshStandardMaterial color="#b8b0a2" />
      </mesh>
    </group>
  );
}
